import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, Plus, Edit2, Trash2, Save, Users, Shield, 
  Building, User, ChevronRight, Loader2, AlertCircle, CheckCircle,
  Bell, X, UserX, UserCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi } from '@/lib/api';
import ConflictStatsPanel from '@/components/ConflictStatsPanel';

const ROLE_OPTIONS = [
  { value: 'company_admin', label: 'Administrador' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
  { value: 'dpo', label: 'DPO' },
  { value: 'case_manager', label: 'Gestor de Caso' },
  { value: 'triage_manager', label: 'Gestor de Triagem' }
];

const DEFAULT_CATEGORIES = [
  "Corrupção e suborno",
  "Fraude financeira",
  "Assédio moral/sexual",
  "Violações ambientais",
  "Violações de segurança de dados",
  "Irregularidades laborais",
  "Conflito de interesses",
  "Abuso de poder",
  "Branqueamento de capitais",
  "Outros"
];

const ConflictsPage = () => {
  const [rules, setRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showEditor, setShowEditor] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    rule_type: 'role',
    blocked_role: '',
    blocked_department: '',
    blocked_person_keywords: [],
    blocked_categories: [],
    blocked_keywords: [],
    fallback_role: '',
    fallback_user_id: '',
    notify_compliance: true,
    notify_triage: true
  });

  const [newKeyword, setNewKeyword] = useState('');
  const [newPersonKeyword, setNewPersonKeyword] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rulesRes, alertsRes, usersRes] = await Promise.all([
        adminApi.getConflictRules(),
        adminApi.getConflictAlerts(),
        adminApi.getUsers()
      ]);
      setRules(rulesRes.data.rules || []);
      setAlerts(alertsRes.data.alerts || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        name: rule.name || '',
        rule_type: rule.rule_type || 'role',
        blocked_role: rule.blocked_role || '',
        blocked_department: rule.blocked_department || '',
        blocked_person_keywords: rule.blocked_person_keywords || [],
        blocked_categories: rule.blocked_categories || [],
        blocked_keywords: rule.blocked_keywords || [],
        fallback_role: rule.fallback_role || '',
        fallback_user_id: rule.fallback_user_id || '',
        notify_compliance: rule.notify_compliance !== false,
        notify_triage: rule.notify_triage !== false
      });
    } else {
      setEditingRule(null);
      setFormData({
        name: '',
        rule_type: 'role',
        blocked_role: '',
        blocked_department: '',
        blocked_person_keywords: [],
        blocked_categories: [],
        blocked_keywords: [],
        fallback_role: '',
        fallback_user_id: '',
        notify_compliance: true,
        notify_triage: true
      });
    }
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      setError('Nome da regra é obrigatório');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingRule) {
        await adminApi.updateConflictRule(editingRule.id, formData);
        setSuccess('Regra atualizada');
      } else {
        await adminApi.createConflictRule(formData);
        setSuccess('Regra criada');
      }
      
      setShowEditor(false);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar regra');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Tem a certeza que pretende eliminar esta regra?')) return;
    
    try {
      await adminApi.deleteConflictRule(ruleId);
      setSuccess('Regra eliminada');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao eliminar regra');
    }
  };

  const handleResolveAlert = async (alertId, action, assigneeId = null) => {
    try {
      await adminApi.resolveConflictAlert(alertId, action, assigneeId);
      setSuccess(action === 'assign' ? 'Conflito resolvido' : 'Alerta ignorado');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao resolver alerta');
    }
  };

  const addKeyword = () => {
    if (newKeyword && !formData.blocked_keywords.includes(newKeyword)) {
      setFormData(prev => ({
        ...prev,
        blocked_keywords: [...prev.blocked_keywords, newKeyword]
      }));
      setNewKeyword('');
    }
  };

  const addPersonKeyword = () => {
    if (newPersonKeyword && !formData.blocked_person_keywords.includes(newPersonKeyword)) {
      setFormData(prev => ({
        ...prev,
        blocked_person_keywords: [...prev.blocked_person_keywords, newPersonKeyword]
      }));
      setNewPersonKeyword('');
    }
  };

  const removeKeyword = (kw) => {
    setFormData(prev => ({
      ...prev,
      blocked_keywords: prev.blocked_keywords.filter(k => k !== kw)
    }));
  };

  const removePersonKeyword = (kw) => {
    setFormData(prev => ({
      ...prev,
      blocked_person_keywords: prev.blocked_person_keywords.filter(k => k !== kw)
    }));
  };

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      blocked_categories: prev.blocked_categories.includes(cat)
        ? prev.blocked_categories.filter(c => c !== cat)
        : [...prev.blocked_categories, cat]
    }));
  };

  const pendingAlerts = alerts.filter(a => a.status === 'PENDENTE');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Panel */}
      <ConflictStatsPanel />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Gestão de Conflitos de Interesses
          </h1>
          <p className="text-slate-500 mt-1">
            Configure regras para detetar e gerir conflitos de interesses na atribuição de denúncias
          </p>
        </div>
        
        <Button onClick={() => openEditor()} data-testid="create-rule-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nova Regra
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">{success}</AlertDescription>
        </Alert>
      )}

      {/* Pending Alerts Banner */}
      {pendingAlerts.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900">
                  {pendingAlerts.length} Alerta(s) de Conflito Pendente(s)
                </h3>
                <p className="text-sm text-amber-700">
                  Existem conflitos de interesses que necessitam de resolução
                </p>
              </div>
              <Badge className="bg-amber-200 text-amber-800">{pendingAlerts.length}</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="rules" className="space-y-4">
        <TabsList>
          <TabsTrigger value="rules" data-testid="tab-rules">
            <Shield className="w-4 h-4 mr-2" />
            Regras ({rules.length})
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Alertas ({alerts.length})
          </TabsTrigger>
        </TabsList>

        {/* Rules Tab */}
        <TabsContent value="rules">
          <div className="grid gap-4">
            {rules.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">Nenhuma regra de conflito definida</p>
                  <Button onClick={() => openEditor()}>Criar Primeira Regra</Button>
                </CardContent>
              </Card>
            ) : (
              rules.map((rule) => (
                <Card key={rule.id} className={`hover:shadow-md transition-shadow ${!rule.is_active ? 'opacity-60' : ''}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        rule.rule_type === 'role' ? 'bg-blue-100' :
                        rule.rule_type === 'department' ? 'bg-purple-100' : 'bg-orange-100'
                      }`}>
                        {rule.rule_type === 'role' ? <Users className="w-5 h-5 text-blue-600" /> :
                         rule.rule_type === 'department' ? <Building className="w-5 h-5 text-purple-600" /> :
                         <User className="w-5 h-5 text-orange-600" />}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{rule.name}</h3>
                          {!rule.is_active && <Badge variant="outline">Inativa</Badge>}
                        </div>
                        
                        <div className="text-sm text-slate-600 space-y-1">
                          {rule.blocked_role && (
                            <p><span className="text-slate-400">Perfil bloqueado:</span> {ROLE_OPTIONS.find(r => r.value === rule.blocked_role)?.label || rule.blocked_role}</p>
                          )}
                          {rule.blocked_department && (
                            <p><span className="text-slate-400">Departamento:</span> {rule.blocked_department}</p>
                          )}
                          {rule.blocked_categories?.length > 0 && (
                            <p><span className="text-slate-400">Categorias:</span> {rule.blocked_categories.slice(0, 3).join(', ')}{rule.blocked_categories.length > 3 ? '...' : ''}</p>
                          )}
                          {rule.blocked_person_keywords?.length > 0 && (
                            <p><span className="text-slate-400">Pessoas/Keywords:</span> {rule.blocked_person_keywords.join(', ')}</p>
                          )}
                          {(rule.fallback_role || rule.fallback_user_id) && (
                            <p><span className="text-slate-400">Substituto:</span> {rule.fallback_role || 'Utilizador específico'}</p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2">
                          {rule.notify_compliance && <Badge variant="outline" className="text-xs">Notifica Compliance</Badge>}
                          {rule.notify_triage && <Badge variant="outline" className="text-xs">Notifica Triagem</Badge>}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditor(rule)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(rule.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <div className="space-y-4">
            {alerts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                  <p className="text-slate-500">Sem alertas de conflito</p>
                </CardContent>
              </Card>
            ) : (
              alerts.map((alert) => (
                <Card key={alert.id} className={`${
                  alert.status === 'PENDENTE' ? 'border-amber-300' :
                  alert.status === 'RESOLVIDO' ? 'border-emerald-200' : 'border-slate-200'
                }`}>
                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        alert.status === 'PENDENTE' ? 'bg-amber-100' :
                        alert.status === 'RESOLVIDO' ? 'bg-emerald-100' : 'bg-slate-100'
                      }`}>
                        {alert.status === 'PENDENTE' ? <AlertTriangle className="w-5 h-5 text-amber-600" /> :
                         alert.status === 'RESOLVIDO' ? <CheckCircle className="w-5 h-5 text-emerald-600" /> :
                         <X className="w-5 h-5 text-slate-500" />}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={
                            alert.conflict_type === 'ai_detected' ? 'bg-violet-50 text-violet-700' : 'bg-blue-50 text-blue-700'
                          }>
                            {alert.conflict_type === 'ai_detected' ? 'Deteção IA' : 'Regra'}
                          </Badge>
                          <Badge variant="outline" className={
                            alert.status === 'PENDENTE' ? 'bg-amber-50 text-amber-700' :
                            alert.status === 'RESOLVIDO' ? 'bg-emerald-50 text-emerald-700' : ''
                          }>
                            {alert.status}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-slate-700 mb-2">{alert.reason}</p>
                        
                        <div className="text-xs text-slate-500 space-y-1">
                          {alert.blocked_user_name && (
                            <p className="flex items-center gap-1">
                              <UserX className="w-3 h-3" />
                              Utilizador em conflito: <strong>{alert.blocked_user_name}</strong>
                            </p>
                          )}
                          {alert.suggested_assignee_name && (
                            <p className="flex items-center gap-1">
                              <UserCheck className="w-3 h-3" />
                              Substituto sugerido: <strong>{alert.suggested_assignee_name}</strong>
                            </p>
                          )}
                        </div>
                        
                        {alert.status === 'PENDENTE' && (
                          <div className="flex items-center gap-2 mt-3">
                            <Select onValueChange={(userId) => handleResolveAlert(alert.id, 'assign', userId)}>
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Atribuir a..." />
                              </SelectTrigger>
                              <SelectContent>
                                {users.filter(u => u.id !== alert.blocked_user_id).map(user => (
                                  <SelectItem key={user.id} value={user.id}>
                                    {user.name} ({user.role})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button variant="outline" size="sm" onClick={() => handleResolveAlert(alert.id, 'ignore')}>
                              Ignorar
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-xs text-slate-400">
                        {new Date(alert.created_at).toLocaleString('pt-PT')}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Rule Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Editar Regra' : 'Nova Regra de Conflito'}</DialogTitle>
            <DialogDescription>
              Defina quando e como detetar conflitos de interesses
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Nome da Regra</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Diretores não tratam denúncias do próprio departamento"
              />
            </div>

            <div>
              <Label>Tipo de Regra</Label>
              <Select value={formData.rule_type} onValueChange={(v) => setFormData(prev => ({ ...prev, rule_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="role">Por Perfil/Role</SelectItem>
                  <SelectItem value="department">Por Departamento</SelectItem>
                  <SelectItem value="person">Por Pessoa Específica</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.rule_type === 'role' && (
              <div>
                <Label>Perfil Bloqueado</Label>
                <Select value={formData.blocked_role} onValueChange={(v) => setFormData(prev => ({ ...prev, blocked_role: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o perfil..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.rule_type === 'department' && (
              <div>
                <Label>Departamento Bloqueado</Label>
                <Input
                  value={formData.blocked_department}
                  onChange={(e) => setFormData(prev => ({ ...prev, blocked_department: e.target.value }))}
                  placeholder="Ex: Recursos Humanos"
                />
              </div>
            )}

            {/* Person Keywords */}
            <div>
              <Label>Palavras-chave de Pessoas (nomes, cargos)</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newPersonKeyword}
                  onChange={(e) => setNewPersonKeyword(e.target.value)}
                  placeholder="Ex: Diretor Financeiro, João Silva..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPersonKeyword())}
                />
                <Button type="button" variant="outline" onClick={addPersonKeyword}>Adicionar</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.blocked_person_keywords.map((kw) => (
                  <Badge key={`pk-${kw}`} variant="secondary" className="flex items-center gap-1">
                    {kw}
                    <button onClick={() => removePersonKeyword(kw)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Blocked Keywords */}
            <div>
              <Label>Palavras-chave na Descrição (trigger)</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Ex: suborno, pagamento ilícito..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                />
                <Button type="button" variant="outline" onClick={addKeyword}>Adicionar</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.blocked_keywords.map((kw) => (
                  <Badge key={`kw-${kw}`} variant="secondary" className="flex items-center gap-1">
                    {kw}
                    <button onClick={() => removeKeyword(kw)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Blocked Categories */}
            <div>
              <Label>Categorias Bloqueadas</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {DEFAULT_CATEGORIES.map(cat => (
                  <label key={cat} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={formData.blocked_categories.includes(cat)}
                      onCheckedChange={() => toggleCategory(cat)}
                    />
                    {cat}
                  </label>
                ))}
              </div>
            </div>

            {/* Fallback */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Perfil Substituto</Label>
                <Select value={formData.fallback_role || '__none__'} onValueChange={(v) => setFormData(prev => ({ ...prev, fallback_role: v === '__none__' ? '' : v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum</SelectItem>
                    {ROLE_OPTIONS.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Utilizador Substituto Específico</Label>
                <Select value={formData.fallback_user_id || '__none__'} onValueChange={(v) => setFormData(prev => ({ ...prev, fallback_user_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.name} ({user.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notifications */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={formData.notify_compliance}
                  onCheckedChange={(c) => setFormData(prev => ({ ...prev, notify_compliance: c }))}
                />
                <span className="text-sm">Notificar Compliance Officer</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={formData.notify_triage}
                  onCheckedChange={(c) => setFormData(prev => ({ ...prev, notify_triage: c }))}
                />
                <span className="text-sm">Notificar Gestor de Triagem</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConflictsPage;
